/**
 * Stage Plot Editor — Left toolbar (palette + stage size + colour).
 */
import { useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { PaletteCategory, PaletteItem, StageElement } from '../../shared/types/stageplot';
import { PALETTE_ITEMS, PALETTE_CATEGORIES, CATEGORY_COLORS } from '../../shared/types/stageplot';
import { StageIcon } from './StageIcon';

export interface ToolbarProps {
  activeCategory: PaletteCategory;
  onCategoryChange: (c: PaletteCategory) => void;
  selectedColor: string;
  onColorChange: (c: string) => void;
  onAddElement: (el: StageElement) => void;
  stageWidth: number;
  stageHeight: number;
  onStageSizeChange: (w: number, h: number) => void;
  onDeleteSelected: () => void;
  hasSelection: boolean;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onDuplicateSelected: () => void;
  snapEnabled: boolean;
  onToggleSnap: (v: boolean) => void;
}

const SWATCHES = ['#4A90D9', '#50B86C', '#9B59B6', '#E67E22', '#E74C3C', '#1ABC9C', '#E91E63', '#F1C40F', '#7F8C8D', '#2C3E50', '#000000'];

export default function Toolbar({
  activeCategory,
  onCategoryChange,
  selectedColor,
  onColorChange,
  onAddElement,
  stageWidth,
  stageHeight,
  onStageSizeChange,
  onDeleteSelected,
  hasSelection,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onDuplicateSelected,
  snapEnabled,
  onToggleSnap,
}: ToolbarProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const items = PALETTE_ITEMS.filter(
    (it) =>
      it.category === activeCategory &&
      (search ? it.label.toLowerCase().includes(search.toLowerCase()) : true),
  );

  const onDragStart = (e: DragEvent<HTMLDivElement>, item: PaletteItem) => {
    e.dataTransfer.setData(
      'application/stageplot-item',
      JSON.stringify({
        iconId: item.iconId,
        category: item.category,
        label: item.label,
        defaultWidth: item.defaultWidth,
        defaultHeight: item.defaultHeight,
        color: selectedColor,
      }),
    );
    e.dataTransfer.effectAllowed = 'copy';
  };

  const onDoubleClick = (item: PaletteItem) => {
    const el: StageElement = {
      id: `el-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 7)}`,
      iconId: item.iconId,
      category: item.category,
      label: item.label,
      x: 0.5,
      y: 0.5,
      width: item.defaultWidth,
      height: item.defaultHeight,
      rotation: 0,
      color: selectedColor,
    };
    onAddElement(el);
  };

  return (
    <div className="stageplot-toolbar">
      <div className="stageplot-toolbar-actions">
        <button className="btn-icon" onClick={onUndo} disabled={!canUndo} title={t('stagePlotEditor.undoTitle')} aria-label={t('stagePlotEditor.undo')}>↩</button>
        <button className="btn-icon" onClick={onRedo} disabled={!canRedo} title={t('stagePlotEditor.redoTitle')} aria-label={t('stagePlotEditor.redo')}>↪</button>
        <div className="stageplot-toolbar-divider" />
        <button className="btn-icon btn-danger-icon" onClick={onDeleteSelected} disabled={!hasSelection} title={t('stagePlotEditor.deleteTitle')} aria-label={t('stagePlotEditor.delete')}>🗑</button>
        <div className="stageplot-toolbar-divider" />
        <button className="btn-icon" onClick={onDuplicateSelected} disabled={!hasSelection} title={t('stagePlotEditor.duplicateTitle')} aria-label={t('stagePlotEditor.duplicate')}>⧉</button>
        <button className={`btn-icon ${snapEnabled ? 'active' : ''}`} onClick={() => onToggleSnap(!snapEnabled)} title={t('stagePlotEditor.toggleSnapTitle')} aria-label={t('stagePlotEditor.toggleSnap')} aria-pressed={snapEnabled}>⊞</button>
      </div>

      {/* Stage size */}
      <div className="stageplot-toolbar-size">
        <label>{t('stagePlotEditor.stage')}</label>
        <div className="stage-size-row">
          <input
            type="number"
            min={1}
            max={40}
            value={stageWidth}
            onChange={(e) => onStageSizeChange(Number(e.target.value) || 1, stageHeight)}
            aria-label={t('stagePlotEditor.stageWidth')}
          />
          <span>×</span>
          <input
            type="number"
            min={1}
            max={40}
            value={stageHeight}
            onChange={(e) => onStageSizeChange(stageWidth, Number(e.target.value) || 1)}
            aria-label={t('stagePlotEditor.stageDepth')}
          />
        </div>
      </div>

      {/* Colour picker */}
      <div className="stageplot-toolbar-color">
        <label>{t('stagePlotEditor.colour')}</label>
        <div className="swatch-row">
          {SWATCHES.map((s) => (
            <button
              key={s}
              className={`swatch ${selectedColor === s ? 'active' : ''}`}
              style={{ background: s }}
              onClick={() => onColorChange(s)}
              title={s}
              aria-label={t('stagePlotEditor.colourValue', { value: s })}
            />
          ))}
        </div>
        <input
          type="color"
          value={selectedColor}
          onChange={(e) => onColorChange(e.target.value)}
          className="color-input"
          aria-label={t('stagePlotEditor.customColour')}
        />
      </div>

      <div className="stageplot-toolbar-search">
        <input
          type="text"
          placeholder={t('stagePlotEditor.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t('stagePlotEditor.searchPalette')}
        />
      </div>

      <nav className="stageplot-toolbar-categories" aria-label={t('stagePlotEditor.elementCategory')}>
        {Object.entries(PALETTE_CATEGORIES).map(([cat, label]) => (
          <button
            key={cat}
            className={`stageplot-category-tab ${cat === activeCategory ? 'active' : ''}`}
            onClick={() => onCategoryChange(cat as PaletteCategory)}
            aria-pressed={cat === activeCategory}
          >
            {label}
          </button>
        ))}
      </nav>

      <div className="stageplot-toolbar-items" role="listbox" aria-label={t('stagePlotEditor.paletteItems')}>
        {items.map((item) => (
          <div
            key={item.iconId}
            className="stageplot-palette-item"
            draggable
            onDragStart={(e) => onDragStart(e, item)}
            onDoubleClick={() => onDoubleClick(item)}
            title={t('stagePlotEditor.itemDragTitle', { label: item.label })}
            role="option"
          >
            <div className="stageplot-palette-icon" style={{ color: selectedColor }}>
              <StageIcon iconId={item.iconId} color={selectedColor} size={28} title={item.label} />
            </div>
            <div className="stageplot-palette-label">{item.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export { CATEGORY_COLORS };
